import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    Text,
    Grid,
    GridItem,
    HStack,
    IconButton,
    useToast,
    Center,
    Spinner,
    Select,
    FormControl,
    FormLabel,
    VStack,
} from '@chakra-ui/react';
import { FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { customAuthAxios } from '@/utils/customAuthAxios';
import { getDjangoToken } from '@/utils/instagram/services/apiService';

interface InstagramDmContent {
    id: number;
    name: string;
    type: string;
    valid_period?: {
        start_at: string;
        end_at: string;
    };
}

export const TableHeaderCell = ({ children, ...props }: any) => (
    <GridItem
        bg="gray.200"
        textAlign="left"
        py={2}
        fontWeight="bold"
        paddingLeft="10px"
        {...props}
    >
        {children}
    </GridItem>
);

const InstagramPackageContents: React.FC = () => {
    const { restaurantId } = useParams<{ restaurantId: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const [dmContents, setDmContents] = useState<InstagramDmContent[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedContent, setSelectedContent] = useState<string>('');

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    useEffect(() => {
        if (restaurantId) {
            fetchDmContents();
        }
    }, [restaurantId]);

    const fetchDmContents = async () => {
        try {
            setIsLoading(true);
            const djangoToken = await getDjangoToken();
            const response = await customAuthAxios.get(
                `/api/restaurants/${restaurantId}/instagram/dm/list_for_bot/`,
                {
                    headers: { Authorization: `Bearer ${djangoToken}` },
                }
            );
            console.log('DM Contents Response:', response.data);
            setDmContents(response.data);
        } catch (error) {
            console.error('Failed to fetch DM contents:', error);
            toast({
                title: 'エラー',
                description: 'DMコンテンツの取得に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (contentId: number) => {
        if (!window.confirm('このDMコンテンツを削除してもよろしいですか？')) {
            return;
        }

        try {
            const djangoToken = await getDjangoToken();
            await customAuthAxios.delete(
                `/api/restaurants/${restaurantId}/instagram/dm/${contentId}/`,
                {
                    headers: { Authorization: `Bearer ${djangoToken}` },
                }
            );
            toast({
                title: '成功',
                description: 'DMコンテンツを削除しました',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            fetchDmContents();
        } catch (error) {
            console.error('Failed to delete DM content:', error);
            toast({
                title: 'エラー',
                description: 'DMコンテンツの削除に失敗しました',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    return (
        <Box p={4} maxW="800px" mx="auto">
            <Card>
                <CardHeader>
                    <Text fontWeight="bold" fontSize="lg">DMコンテンツ選択</Text>
                </CardHeader>
                <CardBody>
                    {isLoading ? (
                        <Center py={4}>
                            <Spinner  />
                        </Center>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            <FormControl>
                                <FormLabel>DMコンテンツ</FormLabel>
                                <Select
                                    value={selectedContent}
                                    onChange={(e) => setSelectedContent(e.target.value)}
                                    placeholder="DMコンテンツを選択"
                                >
                                    {dmContents.map((content) => (
                                        <option key={content.id} value={content.id}>
                                            {content.name} ({content.type})
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        </VStack>
                    )}
                </CardBody>
            </Card>
        </Box>
    );
};

export default InstagramPackageContents; 